import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { Box } from 'grommet';
import { Sun, FormClose } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import RadioToggleButton from './RadioToggleButton';
import { StyledReactTooltip } from './StyledButtons';

const TOOLTIP_ID = 'theme-toggle-tooltip';
const TOOLTIP_DISMISSED_KEY = 'themeTooltipDismissed';

const ThemeToggleButton = ({
  iconSize = '1.4rem',
  iconColor = 'var(--button-icon-color)',
  setUserPreference = () => null,
  showTooltip = false,
  ...rest
}) => {
  const { theme, setDarkMode, isDarkMode } = useAppTheme();
  const { page, button } = theme;

  const useHighlight = isDarkMode ? button.hoverText : page.highlight;

  // Check if tooltip has been dismissed before
  const hasDismissed = React.useMemo(() => {
    try {
      return localStorage.getItem(TOOLTIP_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  }, []);

  // Local state for dismissing the tooltip
  const [tooltipVisible, setTooltipVisible] = React.useState(
    showTooltip && !hasDismissed,
  );

  React.useEffect(() => {
    setTooltipVisible(showTooltip && !hasDismissed);
  }, [showTooltip, hasDismissed]);

  // Show tooltip only if tooltipVisible is true
  React.useEffect(() => {
    if (tooltipVisible) {
      ReactTooltip.show(document.getElementById(TOOLTIP_ID));
    } else {
      ReactTooltip.hide(document.getElementById(TOOLTIP_ID));
    }
  }, [tooltipVisible]);

  const handleDismissTooltip = () => {
    setTooltipVisible(false);
    try {
      localStorage.setItem(TOOLTIP_DISMISSED_KEY, 'true');
    } catch {
      // Fail silently
    }
  };

  const handleThemeToggle = (val) => {
    setDarkMode(val);
    localStorage.setItem('appThemePreference', val ? 'true' : 'false');
    setUserPreference('theme', val ? 'dark' : 'light');
  };

  return (
    <Box direction="row" align="center" gap="0.5rem" {...rest} position="relative">
      <Sun color={iconColor} size={iconSize} />
      <RadioToggleButton
        inactiveBg={button.selectBg}
        activeBg={button.selectBg}
        toggleBg={useHighlight}
        nameKey="app-theme-toggle"
        initialValue={isDarkMode}
        customPad="4px"
        updateParent={(val) => handleThemeToggle(val)}
        id={TOOLTIP_ID}
      />
      <StyledReactTooltip
        clickable
        id={TOOLTIP_ID}
        place="bottom"
        effect="solid"
        arrowColor={button.primaryBg}
        bgColor={button.primaryBg}
        className="sideNavTip"
        overridePosition={({ left, top }) => {
          const offset = 8;
          return { top: top + offset, left };
        }}
        getContent={() => (tooltipVisible ? (
          <Box direction="row" align="center" gap="0.5rem">
            <Box flex="grow">
              <span style={{ color: page.primaryText, fontSize: '0.85rem' }}>
                Change your preferred theme at any time!
              </span>
            </Box>
            <Box
              flex={false}
              style={{ cursor: 'pointer' }}
              onClick={handleDismissTooltip}
              aria-label="Dismiss tooltip"
            >
              <FormClose size="1rem" color={page.primaryText} />
            </Box>
          </Box>
        ) : null)}
        isOpen={tooltipVisible}
      />
    </Box>
  );
};

ThemeToggleButton.propTypes = {
  iconSize: PropTypes.string,
  iconColor: PropTypes.string,
  setUserPreference: PropTypes.func,
  showTooltip: PropTypes.bool,
};

export default ThemeToggleButton;
